import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { MatchRow } from "./types";

export const useMatches = () => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setMatches([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("matches")
      .select("*")
      .is("archived_at", null)
      .order("matched_at", { ascending: false });
    if (error) {
      setMatches([]);
      setLoading(false);
      return;
    }
    const rows = (data as MatchRow[]) ?? [];
    // hydrate other-member profile
    const otherIds = Array.from(
      new Set(
        rows.map((r) => (r.member_a === user.id ? r.member_b : r.member_a)),
      ),
    );
    if (otherIds.length) {
      const { data: profs } = await supabase
        .from("public_profiles")
        .select("id, display_name, photos, profile_id")
        .in("id", otherIds);
      const byId = new Map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (profs as any[] | null)?.map((p) => [p.id, p]) ?? [],
      );
      rows.forEach((r) => {
        const otherId = r.member_a === user.id ? r.member_b : r.member_a;
        r.other = byId.get(otherId);
      });
    }
    setMatches(rows);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const archive = useCallback(
    async (id: string) => {
      const { error } = await supabase
        .from("matches")
        .update({ archived_at: new Date().toISOString() })
        .eq("id", id);
      if (!error) setMatches((prev) => prev.filter((m) => m.id !== id));
      return { error: error?.message ?? null };
    },
    [],
  );

  return { matches, loading, reload: load, archive };
};
